const DB_NAME = "trading-center-cache";
const STORE_NAME = "user-data";
const DB_VERSION = 1;

class DataCache {
  constructor() {
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
    });
  }

  async get(key) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async set(key, value) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(
        {
          data: value,
          timestamp: Date.now(),
        },
        key,
      );

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(key) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = key ? store.delete(key) : store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

const dataCache = new DataCache();

export const getCachedData = async (userId, maxAge = 5 * 60 * 1000) => {
  try {
    const cached = await dataCache.get(`user-data-${userId}`);

    if (!cached) {
      console.log("[Cache] No cached data found");
      return null;
    }

    const age = Date.now() - cached.timestamp;
    const ageMinutes = Math.floor(age / 60000);

    if (age > maxAge) {
      console.log(`[Cache] Data expired (${ageMinutes} minutes old)`);
      await dataCache.clear(`user-data-${userId}`);
      return null;
    }

    console.log(`[Cache] Using cached data (${ageMinutes} minutes old)`);
    return cached.data;
  } catch (error) {
    console.error("[Cache] Read error:", error);
    return null;
  }
};

export const setCachedData = async (userId, data) => {
  try {
    await dataCache.set(`user-data-${userId}`, data);
    const size = new Blob([JSON.stringify(data)]).size;
    const sizeMB = (size / 1024 / 1024).toFixed(2);
    console.log(`[Cache] Stored ${sizeMB} MB of data`);
  } catch (error) {
    console.error("[Cache] Write error:", error);
  }
};

export const invalidateUserCache = async (userId) => {
  try {
    await dataCache.clear(`user-data-${userId}`);
    console.log("[Cache] Invalidated user cache");
  } catch (error) {
    console.error("[Cache] Invalidation error:", error);
  }
};

export const clearAllCache = async () => {
  try {
    await dataCache.clear();
    console.log("[Cache] Cleared all cache");
  } catch (error) {
    console.error("[Cache] Clear all error:", error);
  }
};
